import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App.tsx';
import './index.css';
import '@syncfusion/ej2/tailwind.css';
import '@syncfusion/ej2-layouts/styles/tailwind.css';
import '@syncfusion/ej2-react-gantt/styles/tailwind.css';
import './styles/syncfusion-overrides.css';
import { GlobalAnimationMode, registerLicense, setGlobalAnimation, loadCldr, setCulture } from '@syncfusion/ej2-base';
import { L10n } from '@syncfusion/ej2-base';
import { hydrateAuthStore } from '../store/hydration.ts';
import { startErrorReporter } from './services/ErrorReporter';

// Import CLDR data for German locale
// Order according to Syncfusion docs: numbers, timeZoneNames, ca-gregorian, numberingSystems
import deNumbersData from '@syncfusion/ej2-cldr-data/main/de/numbers.json';
import deTimeZoneNamesData from '@syncfusion/ej2-cldr-data/main/de/timeZoneNames.json';
import deGregorianData from '@syncfusion/ej2-cldr-data/main/de/ca-gregorian.json';
import numberingSystemsData from '@syncfusion/ej2-cldr-data/supplemental/numberingSystems.json';

// Load CLDR data for German locale (only "de" is available in ej2-cldr-data)
// Must be loaded before any Syncfusion components are rendered.
// setCulture must match loaded CLDR; "de-DE" has no separate CLDR, so use "de" for calendar month names etc.
loadCldr(deNumbersData, deTimeZoneNamesData, deGregorianData, numberingSystemsData);
setCulture('de');

// Registering Syncfusion license key
registerLicense('Ngo9BigBOggjHTQxAR8/V1NNaF5cXmBCf1FpRmJGdld5fUVHYVZUTXxaS00DNHVRdkdmWXtecXRdRmhcWU13XENWYUA=');
setGlobalAnimation(GlobalAnimationMode.Disable);

// Set up German locale configuration for Syncfusion components
// Load for both 'de' and 'de-DE' to support different locale settings
L10n.load({
  'de': {
    'dropdowns': {
      'noRecordsTemplate': 'Keine Datensätze gefunden',
      'actionFailureTemplate': 'Fehler beim Laden der Daten'
    },
    'multi-select': {
      'selectAllText': 'Alle auswählen',
      'unSelectAllText': 'Alle abwählen',
      'overflowCountTemplate': '+${count} weitere',
      'totalCountTemplate': '${count} ausgewählt'
    },
    'grid': {
      'noRecords': 'Keine Datensätze gefunden',
      'emptyRecord': 'Keine Datensätze anzuzeigen'
    },
    'datepicker': {
      'today': 'Heute',
      'placeholder': 'Datum auswählen',
      'monthNames': ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'],
      'shortMonthNames': ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez']
    },
    'datetimepicker': {
      'today': 'Heute',
      'placeholder': 'Datum und Uhrzeit auswählen',
      'monthNames': ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'],
      'shortMonthNames': ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez']
    },
    'calendar': {
      'today': 'Heute',
      'monthNames': ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'],
      'shortMonthNames': ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez']
    },
    'schedule': {
      'today': 'Heute',
      'day': 'Tag',
      'week': 'Woche',
      'workWeek': 'Arbeitswoche',
      'month': 'Monat',
      'timelineDay': 'Zeitachse Tag',
      'timelineWeek': 'Zeitachse Woche',
      'timelineMonth': 'Zeitachse Monat',
      'noEvents': 'Keine Ereignisse',
      'emptyContainer': 'An diesem Tag sind keine Ereignisse geplant.',
      'allDay': 'Ganztägig',
      'start': 'Start',
      'end': 'Ende',
      'more': 'mehr',
      'close': 'Schließen',
      'cancel': 'Abbrechen',
      'noTitle': '(Kein Titel)',
      'delete': 'Löschen',
      'edit': 'Bearbeiten',
      'save': 'Speichern',
      'ok': 'OK',
      'yes': 'Ja',
      'no': 'Nein',
      'previous': 'Vorherige',
      'next': 'Nächste',
      'year': 'Jahr',
      'agenda': 'Agenda',
      'date': 'Datum',
      'time': 'Zeit',
      'event': 'Ereignis',
      'events': 'Ereignisse',
      'untitled': 'Unbenannt'
    },
    'gantt': {
      'startDate': 'Start',
      'endDate': 'Ende',
      'duration': 'Dauer',
      'progress': 'Fortschritt',
      'minute': 'Minute',
      'minutes': 'Minuten'
    }
  },
  'de-DE': {
    'grid': {
      'noRecords': 'Keine Datensätze gefunden',
      'emptyRecord': 'Keine Datensätze anzuzeigen',
      'Add': 'Hinzufügen',
      'Edit': 'Bearbeiten',
      'Delete': 'Löschen',
      'Update': 'Speichern',
      'Cancel': 'Abbrechen',
      'Search': 'Suchen',
      'SearchColumns': 'Suchen',
      'FilterButton': 'Filtern',
      'ClearButton': 'Zurücksetzen',
      'StartsWith': 'Beginnt mit',
      'EndsWith': 'Endet mit',
      'Contains': 'Enthält',
      'Equal': 'Gleich',
      'NotEqual': 'Ungleich',
      'LessThan': 'Kleiner als',
      'LessThanOrEqual': 'Kleiner oder gleich',
      'GreaterThan': 'Größer als',
      'GreaterThanOrEqual': 'Größer oder gleich',
      'EmptyRecord': 'Keine Einträge vorhanden',
      'SearchPlaceholder': 'Suchen...',
      'PagerInfo': '{0} von {1} Seiten ({2} Einträge)',
      'FirstPage': 'Erste',
      'LastPage': 'Letzte',
      'NextPage': 'Nächste',
      'PreviousPage': 'Vorherige',
      'Page': 'Seite',
      'Pages': 'Seiten',
      'Items': 'Einträge',
      'DateFormat': 'dd.MM.yyyy HH:mm',
      'DateSeparator': '.',
      'TimeSeparator': ':',
      'Pager': {
        'currentPageInfo': '{0} von {1} Seiten ({2} Einträge)',
        'totalItemsInfo': '({0} Einträge)',
        'firstPageTooltip': 'Zur ersten Seite',
        'lastPageTooltip': 'Zur letzten Seite',
        'nextPageTooltip': 'Zur nächsten Seite',
        'previousPageTooltip': 'Zur vorherigen Seite',
        'nextPagerTooltip': 'Zum nächsten Pager',
        'previousPagerTooltip': 'Zum vorherigen Pager'
      }
    },
    'pager': {
      'currentPageInfo': '{0} von {1} Seiten ({2} Einträge)',
      'totalItemsInfo': '({0} Einträge)',
      'firstPageTooltip': 'Zur ersten Seite',
      'lastPageTooltip': 'Zur letzten Seite',
      'nextPageTooltip': 'Zur nächsten Seite',
      'previousPageTooltip': 'Zur vorherigen Seite',
      'nextPagerTooltip': 'Zum nächsten Pager',
      'previousPagerTooltip': 'Zum vorherigen Pager'
    },
    'datepicker': {
      'today': 'Heute',
      'placeholder': 'Datum auswählen',
      'monthNames': ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'],
      'shortMonthNames': ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez']
    },
    'datetimepicker': {
      'today': 'Heute',
      'placeholder': 'Datum und Uhrzeit auswählen',
      'monthNames': ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'],
      'shortMonthNames': ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez']
    },
    'calendar': {
      'today': 'Heute',
      'monthNames': ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'],
      'shortMonthNames': ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez']
    },
    'dropdownlist': {
      'noRecordsTemplate': 'Keine Datensätze gefunden',
      'actionFailureTemplate': 'Fehler beim Laden der Daten'
    },
    'dropdowns': {
      'noRecordsTemplate': 'Keine Datensätze gefunden',
      'actionFailureTemplate': 'Fehler beim Laden der Daten'
    },
    'multi-select': {
      'selectAllText': 'Alle auswählen',
      'unSelectAllText': 'Alle abwählen',
      'overflowCountTemplate': '+${count} weitere',
      'totalCountTemplate': '${count} ausgewählt'
    },
    'schedule': {
      'today': 'Heute',
      'day': 'Tag',
      'week': 'Woche',
      'workWeek': 'Arbeitswoche',
      'month': 'Monat',
      'timelineDay': 'Zeitachse Tag',
      'timelineWeek': 'Zeitachse Woche',
      'timelineMonth': 'Zeitachse Monat',
      'noEvents': 'Keine Ereignisse',
      'emptyContainer': 'An diesem Tag sind keine Ereignisse geplant.',
      'allDay': 'Ganztägig',
      'start': 'Start',
      'end': 'Ende',
      'more': 'mehr',
      'close': 'Schließen',
      'cancel': 'Abbrechen',
      'noTitle': '(Kein Titel)',
      'delete': 'Löschen',
      'edit': 'Bearbeiten',
      'save': 'Speichern',
      'ok': 'OK',
      'yes': 'Ja',
      'no': 'Nein',
      'previous': 'Vorherige',
      'next': 'Nächste',
      'year': 'Jahr',
      'agenda': 'Agenda',
      'date': 'Datum',
      'time': 'Zeit',
      'event': 'Ereignis',
      'events': 'Ereignisse',
      'untitled': 'Unbenannt'
    },
    'gantt': {
      'startDate': 'Start',
      'endDate': 'Ende',
      'duration': 'Dauer',
      'progress': 'Fortschritt',
      'minute': 'Minute',
      'minutes': 'Minuten'
    }
  }
});


// Gantt tooltips: format helpers (must be on window for Syncfusion template engine)
(globalThis as any).formatTaskbarDateTime = (value: unknown): string => {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value as string | number);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${String(date.getFullYear()).slice(-2)} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};
(globalThis as any).formatTaskbarSubject = (...args: unknown[]): string => {
  const v = args.find(x => x != null && x !== '');
  return v != null ? String(v) : '';
};
(globalThis as any).formatTaskbarProgress = (value: unknown): string => {
  const n = value != null ? Number(value) : 0;
  return String(Number.isNaN(n) ? 0 : n);
};
(globalThis as any).formatTimelineDate = (...args: unknown[]): string => {
  const val = args.find(x => x != null);
  return (globalThis as any).formatTaskbarDateTime(val);
};

// Start error reporter (flushes queued errors from previous sessions)
startErrorReporter();

// Hydrate the auth store before rendering
hydrateAuthStore().then(() => {
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <App />
    </StrictMode>,
  )
});
